import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ruta, idiomaValido } from './rutas';
import TEXTOS from './seoTextos.json';

export const SITE_URL = 'https://www.pacifictourcr.com';
const SITE_NAME = 'Pacific Tour CR';
const IMAGEN_DEFECTO = `${SITE_URL}/logo.png`;

// Crea (si no existe) y actualiza una etiqueta del <head>.
function setTag(selector, crear, atributo, valor) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = crear();
    document.head.appendChild(el);
  }
  el.setAttribute(atributo, valor);
}

const setMeta = (attr, key, content) =>
  setTag(`meta[${attr}="${key}"]`, () => {
    const m = document.createElement('meta');
    m.setAttribute(attr, key);
    return m;
  }, 'content', content);

const setLink = (selector, attrs, href) =>
  setTag(selector, () => {
    const l = document.createElement('link');
    Object.entries(attrs).forEach(([k, v]) => l.setAttribute(k, v));
    return l;
  }, 'href', href);

const urlAbsoluta = (path) => `${SITE_URL}${encodeURI(decodeURI(path))}`;

// Recorta un texto largo para usarlo como descripción (~155 caracteres).
export function resumir(texto, max = 155) {
  const limpio = (texto || '').replace(/\s+/g, ' ').trim();
  if (limpio.length <= max) return limpio;
  return limpio.slice(0, max - 1).replace(/\s+\S*$/, '') + '…';
}

/**
 * Título, descripción, URL canónica, versiones en otros idiomas (hreflang),
 * idioma e imagen para compartir de cada página.
 * - `pagina`: clave de RUTAS (home, todas, detalle, …).
 * - `title`/`description`: opcionales; si faltan se usan los de seoTextos.json.
 * Solo toca etiquetas del <head>; no cambia nada de lo que se ve en la página.
 */
export function useSeo({ pagina, params, title, description, image }) {
  const { i18n } = useTranslation();
  const lang = idiomaValido(i18n.language);
  const textos = TEXTOS[pagina]?.[lang];
  const titulo = title ?? textos?.title;
  const descripcion = description ?? textos?.description;
  const clave = JSON.stringify(params || {});

  useEffect(() => {
    if (!titulo || !pagina) return;
    const p = JSON.parse(clave);
    const tituloCompleto = titulo.includes(SITE_NAME) ? titulo : `${titulo} | ${SITE_NAME}`;
    const url = urlAbsoluta(ruta(pagina, lang, p));

    document.title = tituloCompleto;
    document.documentElement.setAttribute('lang', lang);

    if (descripcion) {
      setMeta('name', 'description', descripcion);
      setMeta('property', 'og:description', descripcion);
    }
    setMeta('property', 'og:title', tituloCompleto);
    setMeta('property', 'og:url', url);
    setMeta('property', 'og:image', image || IMAGEN_DEFECTO);
    setMeta('property', 'og:locale', lang === 'en' ? 'en_US' : 'es_CR');
    setLink('link[rel="canonical"]', { rel: 'canonical' }, url);

    // Versiones de esta misma página en cada idioma.
    setLink('link[rel="alternate"][hreflang="es"]', { rel: 'alternate', hreflang: 'es' },
      urlAbsoluta(ruta(pagina, 'es', p)));
    setLink('link[rel="alternate"][hreflang="en"]', { rel: 'alternate', hreflang: 'en' },
      urlAbsoluta(ruta(pagina, 'en', p)));
    setLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default' },
      urlAbsoluta(ruta(pagina, 'es', p)));
  }, [pagina, clave, titulo, descripcion, image, lang]);
}
